import React, { useState, useEffect, useRef } from "react";
import {
  Form, Input, Button, Card, Select, message, Spin, Modal, Divider,
  Typography, Tabs, Row, Col, Popover,
} from "antd";
import { PlusOutlined, MinusCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import {
  clearHotelProfileProcess,
  hotelProfileContentRequest,
  requestHotelProfileContentUpdateBatch,
  processAction,
} from "@/AppRedux/actions/admin/hotelProfileConfig";
import { useAppDispatch, useAppSelector } from "@/AppRedux/store/hooks";
import { getCurrentLanguage } from "@/modules/i18n/i18n.utils";
import { useIntl } from "react-intl";
import { ImageUploader } from "@/components/ui/ImageUploader";
import { useParams } from "react-router";
import { uploadSingleImageAndGetDownloadUrl } from "@/utils/ImagesUploadToS3";
import { resizeImageToFile } from "@/utils/imageUtils";
import TiptapEditor from "@/components/ui/TiptapEditor";

export const supportedLocales: { [key: string]: { name: string } } = {
  en: { name: "English" },
  th: { name: "Thai" },
  zh: { name: "Chinese" },
  ru: { name: "Russian" },
  ko: { name: "Korean" },
  ja: { name: "Japanese" },
};

type CoverImageState = {
  file: File | null;
  previewUrl?: string;
  existingUrl?: string | null;
};

const arrayToString = (arr: any) =>
  Array.isArray(arr) ? arr.join(", ") : arr || "";

const stringToArray = (str: string) =>
  str
    ? str
      .split(",")
      .map((item: string) => item.trim())
      .filter((item: string) => item)
    : [];

// Contact list helpers (websites/phones/emails)
const ensureAtLeastOne = (arr: any) => {
  const a = Array.isArray(arr) ? arr : [];
  return a.length > 0 ? a : [""];
};

const cleanList = (arr: any) =>
  (Array.isArray(arr) ? arr : [])
    .map((x) => String(x ?? "").trim())
    .filter(Boolean)
    .slice(0, 3);

type ContactListInputProps = {
  name: "phones" | "emails";
  placeholder?: string;
  max?: number; // default 3
  inputType?: React.HTMLInputTypeAttribute;
};

const ContactListInput: React.FC<ContactListInputProps> = ({
  name,
  placeholder,
  max = 3,
  inputType = "text",
}) => {
  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => {
        const canAdd = fields.length < max;

        return (
          <div>
            {fields.map((field, index) => {
              const { key, ...restField } = field;
              return (
                <Row
                  key={key}
                  gutter={8}
                  align="middle"
                  style={{ marginBottom: 8 }}
                >
                  <Col flex="auto">
                    <Form.Item {...restField} style={{ marginBottom: 0 }}>
                      <Input type={inputType} placeholder={placeholder} />
                    </Form.Item>
                  </Col>

                  <Col>
                    <Button
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(field.name)}
                      disabled={fields.length === 1 && index === 0}
                    />
                  </Col>
                </Row>
              );
            })}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => add("")}
              disabled={!canAdd}
              block
            >
              Add (max {max})
            </Button>
          </div>
        );
      }}
    </Form.List>
  );
};


type FieldHintProps = {
  label: string;
  title: string;
  steps: React.ReactNode[];
};

const FieldLabelWithHint: React.FC<FieldHintProps> = ({ label, title, steps }) => (
  <span>
    {label}&nbsp;
    <Popover
      trigger="click"
      title={<strong>{title}</strong>}
      styles={{ root: { maxWidth: 320 } }}
      content={
        <ol style={{ paddingLeft: 16, margin: 0 }}>
          {steps.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </ol>
      }
    >
      <QuestionCircleOutlined style={{ color: '#1677ff', fontSize: 14, cursor: 'pointer' }} />
    </Popover>
  </span>
);

const HotelProfile: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const hotelProfileConfig = useAppSelector((state) => state.hotelProfileConfig);
  /* Get propcode from url */
  const { propcode } = useParams<{ propcode: string }>();

  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [editableLanguages, setEditableLanguages] = useState<string[]>(["en"]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [langToAdd, setLangToAdd] = useState<string | null>(null);

  const [globalData, setGlobalData] = useState<any>({});
  const [profileData, setProfileData] = useState<any>({});
  const [coverImage, setCoverImage] = useState<CoverImageState>({
    file: null,
    previewUrl: undefined,
    existingUrl: undefined,
  });

  const useCurrentFirstRender = useRef(true);

  useEffect(() => {
    if (useCurrentFirstRender.current) {
      dispatch(hotelProfileContentRequest());
    }
    return () => {
      useCurrentFirstRender.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (hotelProfileConfig.process.action && processAction.includes(hotelProfileConfig.process.action)) {
      if (hotelProfileConfig.process.status) {
        if (hotelProfileConfig.process.action === "HOTEL_PROFILE_CONTENT_UPDATE")
          message.success(hotelProfileConfig.process.message)
      }
      else
        Modal.error({
          title: 'Error',
          content: hotelProfileConfig.process.message || 'An error occurred while processing.',
        });

      dispatch(clearHotelProfileProcess());
    }
  }, [hotelProfileConfig.process, dispatch]);


  useEffect(() => {
    const { dataSource } = hotelProfileConfig;
    if (dataSource) {
      const newProfileData: any = {};
      const availableLangs: string[] = [];

      // Global Data
      const newGlobalData = {
        fax: dataSource.fax,
        phones: ensureAtLeastOne(dataSource.phones),
        emails: ensureAtLeastOne(dataSource.emails),
        website: dataSource.website || "",
        social: {
          facebook: dataSource.social_media?.facebook || "",
          instagram: dataSource.social_media?.instagram || "",
          line: dataSource.social_media?.line || "",
          tiktok: dataSource.social_media?.tiktok || "",
        }
      };
      setGlobalData(newGlobalData);

      dataSource.contents.forEach((item: any) => {
        const { lang, title, description, facilities, address } = item;
        availableLangs.push(lang);

        newProfileData[lang] = {
          title: title,
          description: description,
          facilities: arrayToString(facilities),
          address: address,
        };
      });

      setProfileData((prev: any) => ({ ...prev, ...newProfileData }));
      setEditableLanguages((prev) => Array.from(new Set([...prev, ...availableLangs])));

      if (dataSource.cover_image) {
        setCoverImage((prev) => ({ ...prev, existingUrl: dataSource.cover_image }));
      } else {
        setCoverImage((prev) => ({ ...prev, existingUrl: undefined }));
      }
    }
  }, [hotelProfileConfig]);

  useEffect(() => {
    const langData = profileData[currentLang];
    const initialGlobal = Object.keys(globalData).length > 0 ? {
      ...globalData,
      phones: ensureAtLeastOne((globalData as any).phones),
      emails: ensureAtLeastOne((globalData as any).emails),
      website: (globalData as any).website,
    } : {
      fax: "",
      phones: [""],
      emails: [""],
      website: "",
      social: { facebook: "", instagram: "", line: "" ,tiktok: ""}
    };

    if (langData) {
      form.setFieldsValue({ ...initialGlobal, ...langData });
    } else {
      form.setFieldsValue({ ...initialGlobal, title: "", description: "", facilities: "", address: "" });
    }
  }, [currentLang, profileData, globalData, form]);

  const handleLanguageChange = (lang: string) => {
    // Commit current form data before switching
    const currentFormData = form.getFieldsValue();

    const currentGlobal = {
      fax: currentFormData.fax,
      phones: ensureAtLeastOne(currentFormData.phones),
      emails: ensureAtLeastOne(currentFormData.emails),
      website: currentFormData.website,
      social: currentFormData.social
    };
    setGlobalData(currentGlobal);

    setProfileData((prev: any) => ({
      ...prev,
      [currentLang]: currentFormData
    }));
    setCurrentLang(lang);
  };

  const onFinish = async (values: any) => {
    const allData = { ...profileData, [currentLang]: values };
    console.log("Submitting form with allData:", allData);
    const invalidLang = editableLanguages.find(lang => {
      const langContent = allData[lang] || {};
      return !langContent.title || String(langContent.title).trim().length === 0;
    });

    if (invalidLang) {
      message.error(
        intl.formatMessage(
          {
            id: "admin.hotelProfile.requiredIncomplete",
            defaultMessage: "Please fill in required fields for {lang}.",
          },
          {
            lang:
              supportedLocales[invalidLang]?.name || invalidLang.toUpperCase(),
          }
        )
      );

      if (currentLang !== invalidLang) {
        setCurrentLang(invalidLang);
        // The useEffect will handle setting fields
      }
      return;
    }

    const contents = editableLanguages.map((lang) => {
      const langContent = allData[lang] || {};
      return {
        lang,
        title: langContent.title || "",
        description: langContent.description || "",
        facilities: stringToArray(langContent.facilities),
        address: langContent.address || ""
      };
    });

    let backgroundImagePayload: string | null = coverImage.existingUrl || null;

    if (coverImage.file) {
      try {
        const resizedCover = await resizeImageToFile(coverImage.file, 1920, 0.85);
        backgroundImagePayload = await uploadSingleImageAndGetDownloadUrl({
          propCode: propcode || "",
          file: resizedCover,
        });
      } catch (error) {
        console.error("Upload failed:", error);
        Modal.error({
          title: 'Upload Failed',
          content: 'Image upload failed. Please try again or contact support if the problem persists.',
        });
        return; // Stop the submission
      }
    } else if (!coverImage.existingUrl && !coverImage.previewUrl) {
      backgroundImagePayload = null;
    }
    console.log("Final background image URL to save:", backgroundImagePayload);
    const jsonData = {
      cover_image: backgroundImagePayload,
      contents,
      fax: values.fax,
      phones: cleanList(values.phones),
      emails: cleanList(values.emails),
      website: values.website,
      social_media: values.social,
    };

    dispatch(requestHotelProfileContentUpdateBatch(jsonData));
  };

  const showModal = () => setIsModalVisible(true);

  const handleOk = () => {
    if (langToAdd) {
      // Commit current tab's data first
      const currentFormData = form.getFieldsValue();
      setProfileData((prev: any) => ({
        ...prev,
        [currentLang]: currentFormData,
        [langToAdd]: { title: "", description: "", facilities: "", address: "" }
      }));

      if (!editableLanguages.includes(langToAdd)) {
        setEditableLanguages(prev => [...prev, langToAdd]);
      }
      setCurrentLang(langToAdd);

      setIsModalVisible(false);
      setLangToAdd(null);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setLangToAdd(null);
  };

  const handleCoverImageChange = (next: {
    file: File | null;
    previewUrl: string | undefined;
  }) => {
    setCoverImage((prev) => ({ ...prev, ...next }));
  };

  const handleCoverImageRemove = () => {
    setCoverImage({
      file: null,
      previewUrl: undefined,
      existingUrl: undefined,
    });
  };

  const availableLanguagesToAdd = Object.keys(supportedLocales).filter((l) => !editableLanguages.includes(l));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Card title={intl.formatMessage({ id: "admin.hotelProfile.title" })}>
        <Spin spinning={hotelProfileConfig.loading}>
          <Divider>
            {intl.formatMessage({
              id: "admin.hotelProfile.section.background",
              defaultMessage: "Background images",
            })}
          </Divider>
          <div style={{ marginBottom: 16, margin: "auto" }}>
            <ImageUploader
              variant="hero"
              existingUrl={coverImage.existingUrl}
              previewUrl={coverImage.previewUrl}
              onChange={handleCoverImageChange}
              onRemove={handleCoverImageRemove}
            />
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ ...globalData, ...profileData[currentLang] }}
            onValuesChange={(_, allValues) => {
              const patch: any = {};
              if (!Array.isArray(allValues.phones) || allValues.phones.length === 0) patch.phones = [""];
              if (!Array.isArray(allValues.emails) || allValues.emails.length === 0) patch.emails = [""];
              if (Object.keys(patch).length) form.setFieldsValue(patch);
            }}
          >
            <Divider>
              <Typography.Text strong>
                {intl.formatMessage({
                  id: "admin.hotelProfile.section.contentLanguages",
                  defaultMessage: "Hotel Info",
                })}
              </Typography.Text>
            </Divider>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 8 }}>
              {currentLang !== "en" && profileData?.en ? (
                <Button
                  onClick={() => {
                    const source = profileData.en || {};
                    const next = {
                      title: source.title || "",
                      description: source.description || "",
                      facilities: source.facilities || "",
                    };
                    form.setFieldsValue(next);
                    setProfileData((prev: any) => ({
                      ...prev,
                      [currentLang]: { ...prev[currentLang], ...next },
                    }));
                  }}
                >
                  {intl.formatMessage({
                    id: "admin.hotelProfile.copyFromEn",
                    defaultMessage: "Copy from EN",
                  })}
                </Button>
              ) : null}
              <Button
                icon={<PlusOutlined />}
                onClick={showModal}
                disabled={availableLanguagesToAdd.length === 0}
              >
                {intl.formatMessage({
                  id: "admin.hotelProfile.addLanguage",
                  defaultMessage: "Add language",
                })}
              </Button>
            </div>

            <Tabs
              activeKey={currentLang}
              onChange={handleLanguageChange}
              items={editableLanguages.map((lang) => ({
                key: lang,
                label: supportedLocales[lang]?.name || lang.toUpperCase(),
              }))}
            />

            <div style={{ marginTop: 16 }}>
              <Form.Item
                name="title"
                label={intl.formatMessage({
                  id: "admin.hotelProfile.field.title",
                })}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "admin.hotelProfile.field.title.required",
                    }),
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="description"
                label={intl.formatMessage({
                  id: "admin.hotelProfile.field.description",
                })}
                valuePropName="value"
                trigger="onChange"
              >
                <TiptapEditor minHeight={160} disabled={false} />
              </Form.Item>

              <Form.Item
                name="facilities"
                label={intl.formatMessage({
                  id: "admin.hotelProfile.field.facilities",
                  defaultMessage: "Facilities",
                })}
                extra={intl.formatMessage({
                  id: "admin.hotelProfile.field.facilities.hint",
                  defaultMessage: "Use commas to separate each facility.",
                })}
              >
                <Input placeholder="Pool, Wifi, Gym" />
              </Form.Item>
            </div>

            <Divider>
              <Typography.Text strong>
                {intl.formatMessage({ id: "admin.hotelProfile.section.contact", defaultMessage: "Contact Information" })}
              </Typography.Text>
            </Divider>

            <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
              {intl.formatMessage({ id: "admin.hotelProfile.field.address", defaultMessage: "Address" })}
            </Typography.Text>
            {currentLang !== "en" && profileData?.en ? (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
                <Button
                  onClick={() => {
                    const source = profileData.en || {};
                    const next = { address: source.address || "" };
                    form.setFieldsValue(next);
                    setProfileData((prev: any) => ({
                      ...prev,
                      [currentLang]: { ...prev[currentLang], ...next },
                    }));
                  }}
                >
                  {intl.formatMessage({
                    id: "admin.hotelProfile.copyFromEn",
                    defaultMessage: "Copy from EN",
                  })}
                </Button>
              </div>
            ) : null}
            <Tabs
              activeKey={currentLang}
              onChange={handleLanguageChange}
              items={editableLanguages.map((lang) => ({
                key: lang,
                label: supportedLocales[lang]?.name || lang.toUpperCase(),
              }))}
            />
            <Form.Item
              name="address"
              valuePropName="value"
              trigger="onChange"
              style={{ marginTop: 8, marginBottom: 24 }}
            >
              <TiptapEditor minHeight={120} disabled={false} />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="website"
                  label={
                    <FieldLabelWithHint
                      label="Website"
                      title="How to fill in Website"
                      steps={[
                        <>Paste the full URL, <b> ex. <code>https://www.yourhotel.com</code></b></>,
                        <>Or just the domain, <b> ex. <code>yourhotel.com</code></b></>,
                      ]}
                    />
                  }
                >
                  <Input placeholder="www.example.com or https://www.example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="fax" label="Fax">
                  <Input placeholder="+66 2 123 4567" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Phones" extra="Add up to 3 phone numbers.">
                  <ContactListInput
                    name="phones"
                    placeholder="+66 2 123 4567"
                    max={3}
                    inputType="tel"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Emails" extra="Add up to 3 emails.">
                  <ContactListInput
                    name="emails"
                    placeholder="info@example.com"
                    max={3}
                    inputType="email"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong style={{ display: 'block', marginBottom: 16 }}>
              Social Media
            </Typography.Text>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name={['social', 'facebook']}
                  label={
                    <FieldLabelWithHint
                      label="Facebook"
                      title="How to fill in Facebook"
                      steps={[
                        <>Open your Facebook Page and copy the URL from the address bar, <b>ex. <code>https://www.facebook.com/yourhotel</code></b></>,
                        <>Or just paste the Page username/ID shown after <code>facebook.com/</code>, <b>ex. <code>yourhotel</code></b></>,
                      ]}
                    />
                  }
                >
                  <Input prefix={<i className="fab fa-facebook" />} placeholder="Page ID/Name or full Facebook URL" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name={['social', 'instagram']}
                  label={
                    <FieldLabelWithHint
                      label="Instagram"
                      title="How to fill in Instagram"
                      steps={[
                        <>Open your Instagram profile and copy the full URL, <b>ex. <code>https://www.instagram.com/yourhotel</code></b></>,
                        <>Or just paste your username, with or without <code>@</code>, <b>ex. <code>@yourhotel</code></b></>,
                      ]}
                    />
                  }
                >
                  <Input prefix={<i className="fab fa-instagram" />} placeholder="@username or full Instagram URL" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name={['social', 'line']}
                  label={
                    <FieldLabelWithHint
                      label="Line"
                      title="How to fill in LINE"
                      steps={[
                        <>Personal LINE ID: open LINE app → Settings → "Invite Friends" / QR code screen to get your share link</>,
                        <>Official Account: open LINE OA Manager → Settings to get your account link</>,
                        <>Paste the <strong>full link</strong> here, <code>https://line.me/ti/p/~yourid</code> or <code>https://line.me/R/ti/p/@youroaid</code> — a plain LINE ID alone will not work</>,
                      ]}
                    />
                  }
                >
                  <Input prefix={<i className="fab fa-line" />} placeholder="Full LINE link, e.g. https://line.me/ti/p/~yourid" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name={['social', 'tiktok']}
                  label={
                    <FieldLabelWithHint
                      label="TikTok"
                      title="How to fill in TikTok"
                      steps={[
                        <>Open your TikTok profile and copy the full URL, <b>ex. <code>https://www.tiktok.com/@yourhotel</code></b></>,
                        <>Or just paste your username, with or without <code>@</code>, <b>ex. <code>@yourhotel</code></b></>,
                      ]}
                    />
                  }
                >
                  <Input prefix={<i className="fab fa-tiktok" />} placeholder="@username or full TikTok URL" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item style={{ marginTop: 24 }}>
              <Button type="primary" htmlType="submit">
                {intl.formatMessage({
                  id: "admin.hotelProfile.saveAll",
                  defaultMessage: "Save all languages",
                })}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>

      <Modal
        title={intl.formatMessage({
          id: "admin.hotelProfile.addLanguage",
          defaultMessage: "Add Language",
        })}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Select
          placeholder="Select a language"
          style={{ width: "100%" }}
          onChange={(val) => setLangToAdd(val)}
          value={langToAdd}
          options={availableLanguagesToAdd.map((l) => ({
            label: supportedLocales[l].name,
            value: l,
          }))}
        >
        </Select>
      </Modal>
    </div>
  );
};

export default HotelProfile;
